import { useState, type ReactNode } from 'react';
import { BookOpen, IndianRupee, Languages, Puzzle, Users } from 'lucide-react';
import { Controller, type UseFormReturn } from 'react-hook-form';

import { Badge } from '@/components/ui/Badge';
import { FormSection } from '@/components/ui/FormSection';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Link } from 'react-router-dom';
import { boardLabel, type Board, type Option } from '@/constants/institute';
import type { SubjectCategory } from '@/types/subject';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  MAX_ADMISSION_AGE,
  MAX_DESCRIPTION,
  MAX_FEE,
  MIN_ADMISSION_AGE,
  type ClassFormValues,
  type CurriculumFieldName,
} from '../schemas/class.schema';
import { CLASS_CATEGORY_OPTIONS, CLASS_COLOURS, CLASS_ICONS } from '../constants';
import { toAmount, totalIntake } from '../utils';
import {
  ManageSubjectsModal,
  type SubjectChange,
} from '@/features/institute/subjects/components/ManageSubjectsModal';
import { CurriculumListField } from './CurriculumListField';
import { SectionsField } from './SectionsField';

export interface ClassFieldsProps {
  form: UseFormReturn<ClassFormValues>;
}

/**
 * The fields that describe a class, in the order an institute thinks about one:
 * what it is and how it is recognised, how it is split, what it teaches, what it
 * costs.
 *
 * Four components rather than one, because the setup screen asks them a step at
 * a time. They stay in this one file so that the question "which fields make up
 * a class?" still has a single answer to read (CLAUDE.md §3) — splitting them
 * across four files is how one of them quietly grows a field the others do not
 * know about.
 */

// --- step 1 -----------------------------------------------------------------

export interface ClassDetailsSectionProps extends ClassFieldsProps {
  /** The class names this class may take — see `classNameOptions`. */
  nameOptions: Option[];
  /** The school's board, which every class follows. Null while it loads. */
  schoolBoard: Board | null;
  /** The mediums this class may be taught in — see `schoolMediumOptions`. */
  mediumOptions: Option[];
}

export function ClassDetailsSection({ form, nameOptions, schoolBoard, mediumOptions }: ClassDetailsSectionProps) {
  const {
    register,
    control,
    formState: { errors },
  } = form;

  return (
    <div className="flex flex-col gap-5">
      <FormSection
        title="Class details"
        description="What this class is called, who it admits, and how it is filed."
        columns={2}
      >
        <Select
          label="Class name"
          placeholder="Choose a class"
          options={nameOptions}
          error={errors.name?.message}
          {...register('name')}
        />
        <Input
          label="Class code (optional)"
          placeholder="C8"
          autoComplete="off"
          className="font-mono uppercase"
          hint="A short internal reference. Must be unique in your institute."
          error={errors.code?.message}
          {...register('code')}
        />

        <Select
          label="Class category"
          options={CLASS_CATEGORY_OPTIONS}
          hint="Left blank, this is worked out from the class name."
          error={errors.category?.message}
          {...register('category')}
        />
        <Input
          label="Display order (optional)"
          type="number"
          inputMode="numeric"
          min={0}
          placeholder="8"
          className="font-mono"
          hint="Classes do not sort by name — this is the order they are listed in."
          error={errors.displayOrder?.message}
          {...register('displayOrder')}
        />

        <Input
          label="Minimum age (years)"
          type="number"
          inputMode="numeric"
          min={MIN_ADMISSION_AGE}
          max={MAX_ADMISSION_AGE}
          placeholder="13"
          className="font-mono"
          hint="Completed years on the date of admission."
          error={errors.minAgeYears?.message}
          {...register('minAgeYears')}
        />
        <Input
          label="Maximum age (years)"
          type="number"
          inputMode="numeric"
          min={MIN_ADMISSION_AGE}
          max={MAX_ADMISSION_AGE}
          placeholder="14"
          className="font-mono"
          hint="Leave both blank if this class has no age rule."
          error={errors.maxAgeYears?.message}
          {...register('maxAgeYears')}
        />
      </FormSection>

      <FormSection
        title="Board and medium"
        description="Every class follows your school's board. Set a medium only where this class differs."
        columns={2}
      >
        {/* Shown, not chosen: a school is affiliated to one board, so it is set
            once on the Institute Profile rather than per class. */}
        <div className="flex flex-col gap-1.5">
          <span className="lbl">Board / curriculum</span>
          <p className="text-sm font-semibold text-ink">{boardLabel(schoolBoard)}</p>
          <p className="text-sm text-ink-2">
            Your school's board.{' '}
            <Link to="/admin/institute" className="font-semibold text-accent hover:underline">
              Change it on the Institute Profile
            </Link>
            .
          </p>
        </div>
        {/* A school teaching in one medium has nothing to choose, so it is shown
            the way the board is. A class already saved in another medium still
            gets the dropdown, because that medium is then one of the options. */}
        {mediumOptions.length > 1 ? (
          <Select
            label="Medium of instruction"
            placeholder="Same as the institute"
            options={[{ value: '', label: 'Same as the institute' }, ...mediumOptions]}
            error={errors.medium?.message}
            {...register('medium')}
          />
        ) : (
          <div className="flex flex-col gap-1.5">
            <span className="lbl">Medium of instruction</span>
            <p className="text-sm font-semibold text-ink">{mediumOptions[0]?.label}</p>
            <p className="text-sm text-ink-2">
              Your school's only medium.{' '}
              <Link to="/admin/institute" className="font-semibold text-accent hover:underline">
                Add another on the Institute Profile
              </Link>
              .
            </p>
          </div>
        )}
      </FormSection>

      <FormSection
        title="Notes and appearance"
        description="How this class is described and recognised across the product."
        columns={1}
      >
        <DescriptionField form={form} />

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Controller
            name="iconKey"
            control={control}
            render={({ field }) => (
              <fieldset className="flex flex-col gap-2">
                <legend className="lbl mb-2">Icon</legend>
                <div className="flex flex-wrap gap-2">
                  {CLASS_ICONS.map((entry) => {
                    const Icon = entry.icon;
                    const isChosen = field.value === entry.key;

                    return (
                      <button
                        key={entry.key}
                        type="button"
                        aria-label={entry.label}
                        aria-pressed={isChosen}
                        // Choosing the chosen one clears it, so an institute that
                        // wants no icon is not stuck with the first one it tried.
                        onClick={() => field.onChange(isChosen ? '' : entry.key)}
                        className={cn(
                          'grid h-10 w-10 cursor-pointer place-items-center rounded-xl border transition-all',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
                          isChosen
                            ? 'border-accent bg-accent-soft text-accent shadow-sm'
                            : 'border-line-strong bg-surface text-ink-3 hover:border-accent-line hover:text-ink',
                        )}
                      >
                        <Icon className="h-[18px] w-[18px]" aria-hidden />
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            )}
          />

          <Controller
            name="colourKey"
            control={control}
            render={({ field }) => (
              <fieldset className="flex flex-col gap-2">
                <legend className="lbl mb-2">Colour</legend>
                <div className="flex flex-wrap gap-2">
                  {CLASS_COLOURS.map((entry) => {
                    const isChosen = field.value === entry.key;

                    return (
                      <button
                        key={entry.key}
                        type="button"
                        aria-label={entry.label}
                        aria-pressed={isChosen}
                        onClick={() => field.onChange(isChosen ? '' : entry.key)}
                        className={cn(
                          'h-10 w-10 cursor-pointer rounded-xl border-2 transition-all',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
                          entry.swatch,
                          isChosen
                            ? 'border-ink scale-105 shadow-md'
                            : 'border-transparent hover:scale-105',
                        )}
                      />
                    );
                  })}
                </div>
              </fieldset>
            )}
          />
        </div>
      </FormSection>
    </div>
  );
}

/** The description, with the character count the field is capped at. */
function DescriptionField({ form }: ClassFieldsProps) {
  const {
    register,
    watch,
    formState: { errors },
  } = form;

  const typed = (watch('description') ?? '').length;

  return (
    <div className="flex flex-col gap-1">
      <Textarea
        label="Description (optional)"
        rows={3}
        placeholder="Key highlights, or any special notes about this class…"
        error={errors.description?.message}
        {...register('description')}
      />
      {/* Counted up rather than down: an institute writing a note wants to know
          how much room is left, not how much it has used. */}
      <p
        className={cn(
          'self-end font-mono text-xs',
          typed > MAX_DESCRIPTION ? 'text-crit' : 'text-ink-3',
        )}
      >
        {typed} / {MAX_DESCRIPTION}
      </p>
    </div>
  );
}

// --- step 2 -----------------------------------------------------------------

export function ClassSectionsSection({ form }: ClassFieldsProps) {
  const intake = totalIntake(form.watch('sections') ?? []);

  return (
    <FormSection
      title="Sections and seats"
      description="The sections this class runs, and how many students each one takes."
      columns={1}
      aside={
        <Badge tone="accent">
          <Users className="h-3 w-3" aria-hidden />
          {intake} seats
        </Badge>
      }
    >
      <SectionsField form={form} />
    </FormSection>
  );
}

// --- step 3 -----------------------------------------------------------------

export interface ClassCurriculumSectionProps extends ClassFieldsProps {
  /** The school's subject list — each list below offers its own category only. */
  subjects?: { name: string; category: SubjectCategory }[];
  /** Called after the school's list changes, so `subjects` can be read again. */
  onSubjectsChanged?: () => void;
  /**
   * A class that already exists: Manage leaves for the Subjects page with this
   * class picked, rather than opening the school's list here. A class still
   * being created has nothing to pick yet, so it keeps the dialog.
   */
  onManageClassSubjects?: () => void;
}

/** The three lists, one per subject category. */
const CURRICULUM_LISTS: {
  category: SubjectCategory;
  name: CurriculumFieldName;
  label: string;
  emptyText: string;
  icon: ReactNode;
}[] = [
  {
    category: 'CORE',
    name: 'coreSubjects',
    label: 'Core subjects',
    emptyText: 'No core subjects yet — add the ones every student in this class studies.',
    icon: <BookOpen className="h-3.5 w-3.5" />,
  },
  {
    category: 'ELECTIVE',
    name: 'electives',
    label: 'Electives',
    emptyText: 'No electives yet — add the subjects a student may choose.',
    icon: <Puzzle className="h-3.5 w-3.5" />,
  },
  {
    category: 'LANGUAGE',
    name: 'languages',
    label: 'Languages',
    emptyText: 'No languages yet — add the ones offered to this class.',
    icon: <Languages className="h-3.5 w-3.5" />,
  },
];

const LIST_FOR: Record<SubjectCategory, CurriculumFieldName> = {
  CORE: 'coreSubjects',
  ELECTIVE: 'electives',
  LANGUAGE: 'languages',
};

export function ClassCurriculumSection({
  form,
  subjects = [],
  onSubjectsChanged,
  onManageClassSubjects,
}: ClassCurriculumSectionProps) {
  const [managing, setManaging] = useState<SubjectCategory | null>(null);

  const namesIn = (category: SubjectCategory) =>
    subjects.filter((subject) => subject.category === category).map((subject) => subject.name);

  /**
   * Keeps this unsaved class in step with the school's list.
   *
   * The class holds its subjects by name, so a subject renamed while it is
   * picked here would otherwise be sent under a name the list no longer has,
   * and refused on save. A subject moved to another category comes off this
   * list and can be picked from its new one.
   */
  function followChange(change: SubjectChange) {
    const listName = (subject: { category: SubjectCategory }) => LIST_FOR[subject.category];
    const without = (name: CurriculumFieldName, subjectName: string) =>
      (form.getValues(name) ?? []).filter(
        (entry) => entry.name.trim().toLowerCase() !== subjectName.toLowerCase(),
      );

    if (change.kind === 'edited') {
      const name = listName(change.before);
      if (change.after.category !== change.before.category) {
        form.setValue(name, without(name, change.before.name), { shouldDirty: true });
      } else {
        form.setValue(
          name,
          (form.getValues(name) ?? []).map((entry) =>
            entry.name.trim().toLowerCase() === change.before.name.toLowerCase()
              ? { ...entry, name: change.after.name }
              : entry,
          ),
          { shouldDirty: true },
        );
      }
    }
    onSubjectsChanged?.();
  }

  return (
    <FormSection
      title="Subjects, electives and languages"
      description="What this class studies. Core subjects are taught to everyone; electives and languages are chosen by the student."
      columns={1}
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {CURRICULUM_LISTS.map((list) => (
          <CurriculumListField
            key={list.name}
            form={form}
            name={list.name}
            label={list.label}
            emptyText={list.emptyText}
            subjects={namesIn(list.category)}
            icon={list.icon}
            onManage={
              onManageClassSubjects ??
              (onSubjectsChanged ? () => setManaging(list.category) : undefined)
            }
            manageLabel={
              onManageClassSubjects ? "Manage this class's subjects on the Subjects page" : undefined
            }
          />
        ))}
      </div>

      {managing && (
        <ManageSubjectsModal
          isOpen
          category={managing}
          onClose={() => setManaging(null)}
          onChanged={followChange}
        />
      )}
    </FormSection>
  );
}

// --- step 4 -----------------------------------------------------------------

export function ClassFeesSection({ form }: ClassFieldsProps) {
  const {
    register,
    watch,
    formState: { errors },
  } = form;

  const yearlyFee =
    toAmount(watch('tuitionFee')) + toAmount(watch('booksFee')) + toAmount(watch('otherFees'));

  return (
    <FormSection
      title="Fees for the year"
      description="What a student in this class is charged for a full academic year."
      columns={2}
      aside={<Badge tone="accent">{formatCurrency(yearlyFee)} a year</Badge>}
    >
      <Input
        label="Tuition fee (₹)"
        type="number"
        inputMode="numeric"
        min={0}
        max={MAX_FEE}
        placeholder="36000"
        className="font-mono"
        leftIcon={<IndianRupee className="h-4 w-4" />}
        error={errors.tuitionFee?.message}
        {...register('tuitionFee')}
      />
      <Input
        label="Books and study materials (₹)"
        type="number"
        inputMode="numeric"
        min={0}
        max={MAX_FEE}
        placeholder="6500"
        className="font-mono"
        leftIcon={<IndianRupee className="h-4 w-4" />}
        hint="Enter 0 if books are billed separately."
        error={errors.booksFee?.message}
        {...register('booksFee')}
      />
      <Input
        label="Other fees (₹)"
        type="number"
        inputMode="numeric"
        min={0}
        max={MAX_FEE}
        placeholder="1500"
        className="font-mono"
        leftIcon={<IndianRupee className="h-4 w-4" />}
        hint="Laboratory, library, sports or examination. Leave blank if none."
        error={errors.otherFees?.message}
        {...register('otherFees')}
      />

      {/* Read-only, and drawn as a field so it lines up with the three it sums. */}
      <div className="flex flex-col gap-1.5">
        <span className="lbl">Total annual fees</span>
        <output className="flex h-11 items-center rounded-field border border-line-strong bg-surface-2 px-3 font-mono text-base font-bold text-ink">
          {formatCurrency(yearlyFee)}
        </output>
        <p className="text-xs text-ink-3">All three heads, for one academic year.</p>
      </div>
    </FormSection>
  );
}
